import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Tab, 
  Tabs, 
  CircularProgress, 
  Avatar, 
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Chip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService, safeApiCall } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { normalizeImageUrl } from '../components/Post';
import { processEmojiInText } from '../utils/textUtils';
import '../components/Rankings.css';

// ランキングページ専用のスタイル
const rankingsPageStyles = {
  listItem: {
    padding: '16px 10px 16px 56px',
    position: 'relative',
    cursor: 'pointer',
  },
  listItemTop: {
    padding: '16px 10px 16px 56px',
    position: 'relative',
    marginBottom: '8px',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  rankDiv: {
    position: 'absolute',
    left: '10px',
    top: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
};

function Rankings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  // 初期タブ: URL の ?tab= があればそれ、無ければクチコミランキング（重要度が高いため先頭・既定）
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      if (t && ['reviews', 'posts', 'followers', 'stores'].includes(t)) return t;
    } catch (e) {}
    return 'reviews';
  });
  const [rankings, setRankings] = useState({
    followers: [],
    posts: [],
    stores: [],
    reviews: []
  });
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [followingStatus, setFollowingStatus] = useState({});
  const [likeStatus, setLikeStatus] = useState({});
  const [selectedPrefecture, setSelectedPrefecture] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingArea, setLoadingArea] = useState(false);

  // セッションストレージからフォロー状態を取得する関数
  const getFollowStateFromCache = (targetUsername) => {
    if (typeof window !== 'undefined' && window.followStateCache) {
      const state = window.followStateCache.get(targetUsername);
      if (state !== undefined) {
        return state;
      }
    }
    return null;
  };

  // セッションストレージにフォロー状態を保存する関数
  const saveFollowStateToCache = (targetUsername, state) => {
    if (typeof window !== 'undefined' && window.followStateCache) {
      window.followStateCache.set(targetUsername, state);
    }
  };

  // 初期表示時にランキングとエリア情報を取得
  useEffect(() => {
    fetchAreaData();
    fetchRankings();
    fetchReviewsRanking();
  }, []);

  // URL の ?tab= が変わったらタブを同期（サイドバーの「クチコミランキング」導線用）
  useEffect(() => {
    try {
      const t = new URLSearchParams(location.search).get('tab');
      if (t && ['reviews', 'posts', 'followers', 'stores'].includes(t) && t !== activeTab) {
        setActiveTab(t);
        if (t === 'reviews') fetchReviewsRanking();
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 都道府県と市区町村の情報を取得
  const fetchAreaData = async () => {
    setLoadingArea(true);
    try {
      // 都道府県一覧を取得
      const prefecturesResponse = await safeApiCall.get('/api/areas/prefectures');
      setPrefectures(prefecturesResponse.data || []);

      // 現在のエリア設定を取得
      try {
        const cachedSettings = localStorage.getItem('areaSettings');
        if (cachedSettings) {
          const { prefecture, city } = JSON.parse(cachedSettings);
          setSelectedPrefecture(prefecture || '');
          
          // 都道府県が選択されている場合、その市区町村一覧を取得
          if (prefecture) {
            const citiesResponse = await safeApiCall.get(`/api/areas/cities/${prefecture}`);
            setCities(citiesResponse.data || []);
            setSelectedCity(city || '');
          }
        }
      } catch (err) {
        
      }
    } catch (error) {
      
    } finally {
      setLoadingArea(false);
    }
  };

  // 都道府県が変更された時に市区町村一覧を取得
  useEffect(() => {
    const fetchCities = async () => {
      if (selectedPrefecture) {
        setLoadingArea(true);
        try {
          const response = await safeApiCall.get(`/api/areas/cities/${selectedPrefecture}`);
          setCities(response.data || []);
          setSelectedCity('');
        } catch (error) {
          
        } finally {
          setLoadingArea(false);
        }
      } else {
        setCities([]);
        setSelectedCity('');
      }
    };

    fetchCities();
  }, [selectedPrefecture]);

  // ランキングデータを取得する関数
  const fetchRankings = async () => {
    setLoading(true);

    try {
      // フォロワー数ランキングを取得
      const followersResponse = await safeApiCall.get('/api/stats/rankings', { 
        params: { 
          type: 'followers',
          prefecture: selectedPrefecture,
          city: selectedCity,
          limit: 20
        } 
      });
      
      // 人気投稿ランキングを取得
      const postsResponse = await safeApiCall.get('/api/stats/rankings', { 
        params: { 
          type: 'posts',
          prefecture: selectedPrefecture,
          city: selectedCity,
          limit: 20
        } 
      });
      
      // 人気店舗ランキングを取得
      const storesResponse = await safeApiCall.get('/api/stats/rankings', { 
        params: { 
          type: 'stores',
          prefecture: selectedPrefecture,
          city: selectedCity,
          limit: 20
        } 
      });
      
      // データを設定
      if (followersResponse.data && followersResponse.data.status === 'success') {
        setRankings(prevState => ({
          ...prevState,
          followers: followersResponse.data.data || []
        }));
        
        // フォロー状態を設定
        const followerStatus = {};
        followersResponse.data.data.forEach(user => {
          followerStatus[user.id] = user.is_following;
          // キャッシュにも保存
          saveFollowStateToCache(user.username, user.is_following);
        });
        
        setFollowingStatus(prevState => ({
          ...prevState,
          ...followerStatus
        }));
      }
      
      if (postsResponse.data && postsResponse.data.status === 'success') {
        setRankings(prevState => ({
          ...prevState,
          posts: postsResponse.data.data || []
        }));
        
        // いいね状態を設定
        const likeStatusData = {};
        postsResponse.data.data.forEach(post => {
          likeStatusData[post.id] = post.is_liked;
        });
        
        setLikeStatus(likeStatusData);
      }
      
      if (storesResponse.data && storesResponse.data.status === 'success') {
        setRankings(prevState => ({
          ...prevState,
          stores: storesResponse.data.data || []
        }));
        
        // フォロー状態を設定
        const storeStatus = {};
        storesResponse.data.data.forEach(store => {
          storeStatus[store.id] = store.is_following;
          // キャッシュにも保存
          saveFollowStateToCache(store.username, store.is_following);
        });
        
        setFollowingStatus(prevState => ({
          ...prevState,
          ...storeStatus
        }));
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  // クチコミランキングを取得する関数
  const fetchReviewsRanking = async () => {
    setReviewsLoading(true);
    try {
      const response = await apiService.get('/api/reviews-ranking', {
        params: {
          limit: 20,
          prefecture: selectedPrefecture,
          city: selectedCity,
        }
      });
      // apiService.get はレスポンスボディを直接返す（response.data の場合にも対応）
      const data = response?.ranking ? response : (response?.data || {});
      const list = data.ranking || [];
      setRankings(prevState => ({
        ...prevState,
        reviews: list
      }));
    } catch (error) {
      console.error('クチコミランキング取得エラー:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  // タブ切り替え
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // クチコミランキングタブを開いた時に取得（毎回最新化）
    if (newValue === 'reviews') {
      fetchReviewsRanking();
    }
  };

  // 都道府県選択
  const handlePrefectureChange = (event) => {
    setSelectedPrefecture(event.target.value);
  };

  // 市区町村選択
  const handleCityChange = (event) => {
    setSelectedCity(event.target.value);
  };

  // エリア選択後のランキング更新（クチコミランキングもエリアで絞り込む）
  const handleAreaUpdate = () => {
    fetchRankings();
    fetchReviewsRanking();
  };

  // ユーザープロフィールへのナビゲーション
  const handleUserClick = (username) => {
    navigate(`/profile/${username}`);
  };

  // 投稿詳細へのナビゲーション
  const handlePostClick = (postId) => {
    navigate(`/post/${postId}`);
  };

  // フォローボタンのクリック処理
  const handleFollowClick = async (userId, username) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // 現在のフォロー状態を取得
    const isFollowing = followingStatus[userId] || false;

    try {
      if (isFollowing) {
        // アンフォロー処理
        await safeApiCall.delete(`/api/social/follow/${username}`);
      } else {
        // フォロー処理
        await safeApiCall.post(`/api/social/follow/${username}`);
      }

      // フォロー状態を更新
      setFollowingStatus(prevState => ({
        ...prevState,
        [userId]: !isFollowing
      }));

      // グローバルキャッシュも更新
      saveFollowStateToCache(username, !isFollowing);
    } catch (error) {
      
    }
  };

  // いいねボタンのクリック処理
  const handleLikeClick = async (postId) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // 現在のいいね状態を取得
    const isLiked = likeStatus[postId] || false;

    try {
      if (isLiked) {
        // いいね解除処理
        await safeApiCall.delete(`/api/posts/${postId}/like`);
      } else {
        // いいね処理
        await safeApiCall.post(`/api/posts/${postId}/like`);
      }

      // いいね状態を更新
      setLikeStatus(prevState => ({
        ...prevState,
        [postId]: !isLiked
      }));

      // いいね数も更新
      setRankings(prevState => {
        const newPosts = prevState.posts.map(post => {
          if (post.id === postId) {
            return {
              ...post,
              like_count: isLiked ? post.like_count - 1 : post.like_count + 1
            };
          }
          return post;
        });
        return {
          ...prevState,
          posts: newPosts
        };
      });
    } catch (error) {
      
    }
  };

  // フォロワー数ランキングの表示
  const renderFollowerRanking = () => {
    if (rankings.followers.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>表示するランキングがありません</Typography>
        </Box>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {rankings.followers.filter(Boolean).map((user, index) => {
          // 1-3位用の特別なクラスを適用
          const isTop3 = index < 3;
          const rankClass = isTop3 ? `rankings__rank-${index + 1}` : '';
          const itemClass = isTop3 ? `rankings__item-top rankings__item-${index + 1}` : '';
          
          return (
            <React.Fragment key={user.id}>
              <ListItem 
                alignItems="flex-start"
                sx={isTop3 ? rankingsPageStyles.listItemTop : rankingsPageStyles.listItem}
                onClick={() => handleUserClick(user.username)}
                className={itemClass}
              >
                <div className={`rankings__rank ${rankClass}`} style={rankingsPageStyles.rankDiv}>
                  <Typography variant="h6">{index + 1}</Typography>
                </div>
                <ListItemAvatar>
                  <Avatar alt={user.display_name} src={normalizeImageUrl(user.avatar)} />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography>
                      {(user.stage_name && user.shop_name) ? `${user.stage_name}＠${user.shop_name}` : (user.display_name || user.stage_name || user.username)} {user.verified && '✓'} 
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        @{user.username}
                      </Typography>
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      フォロワー: {(user.followers_count || user.follower_count || 0).toLocaleString()}
                    </Typography>
                  }
                />
                {currentUser && currentUser.username !== user.username && (
                  <Button
                    variant={followingStatus[user.id] ? "outlined" : "contained"}
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFollowClick(user.id, user.username);
                    }}
                    sx={{ ml: 2 }}
                  >
                    {followingStatus[user.id] ? "フォロー中" : "フォローする"}
                  </Button>
                )}
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  // 人気投稿ランキングの表示
  const renderPostRanking = () => {
    if (rankings.posts.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>表示するランキングがありません</Typography>
        </Box>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {rankings.posts.filter(Boolean).map((post, index) => {
          // 1-3位用の特別なクラスを適用
          const isTop3 = index < 3;
          const rankClass = isTop3 ? `rankings__rank-${index + 1}` : '';
          const itemClass = isTop3 ? `rankings__item-top rankings__item-${index + 1}` : '';
          const author = post.author || { display_name: post.display_name || '', username: post.username || '', shop_name: post.shop_name || '', stage_name: post.stage_name || '', avatar: post.avatar || '', verified: false };
          
          return (
            <React.Fragment key={post.id}>
              <ListItem 
                alignItems="flex-start"
                sx={isTop3 ? rankingsPageStyles.listItemTop : rankingsPageStyles.listItem}
                onClick={() => handlePostClick(post.id)}
                className={itemClass}
              >
                <div className={`rankings__rank ${rankClass}`} style={rankingsPageStyles.rankDiv}>
                  <Typography variant="h6">{index + 1}</Typography>
                </div>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', ml: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar alt={author.display_name} src={normalizeImageUrl(author.avatar)} sx={{ mr: 1 }} />
                    <Box>
                      <Typography>
                        {(author.stage_name && author.shop_name) ? `${author.stage_name}＠${author.shop_name}` : (author.display_name || author.stage_name || author.username)} {author.verified && '✓'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        @{author.username}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography 
                    variant="body1" 
                    sx={{ mb: 1 }}
                    dangerouslySetInnerHTML={{ __html: processEmojiInText(post.content || post.text || '') }}
                  />
                  {post.image_url && (
                    <Box sx={{ mb: 1, maxHeight: 200, overflow: 'hidden', borderRadius: 2 }}>
                      <img 
                        src={normalizeImageUrl(post.image_url)} 
                        alt="投稿画像" 
                        style={{ width: '100%', objectFit: 'cover' }} 
                      />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      いいね: {(post.likes_count || post.like_count || 0).toLocaleString()}
                    </Typography>
                    <Button
                      variant="text"
                      color={likeStatus[post.id] ? "error" : "inherit"}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLikeClick(post.id);
                      }}
                    >
                      {likeStatus[post.id] ? "♥" : "♡"}
                    </Button>
                  </Box>
                </Box>
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  // 人気店舗ランキングの表示
  const renderStoreRanking = () => {
    if (rankings.stores.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>表示するランキングがありません</Typography>
        </Box>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {rankings.stores.filter(Boolean).map((store, index) => {
          // 1-3位用の特別なクラスを適用
          const isTop3 = index < 3;
          const rankClass = isTop3 ? `rankings__rank-${index + 1}` : '';
          const itemClass = isTop3 ? `rankings__item-top rankings__item-${index + 1}` : '';
          
          return (
            <React.Fragment key={store.id}>
              <ListItem 
                alignItems="flex-start"
                sx={isTop3 ? rankingsPageStyles.listItemTop : rankingsPageStyles.listItem}
                onClick={() => handleUserClick(store.username)}
                className={itemClass}
              >
                <div className={`rankings__rank ${rankClass}`} style={rankingsPageStyles.rankDiv}>
                  <Typography variant="h6">{index + 1}</Typography>
                </div>
                <ListItemAvatar>
                  <Avatar alt={store.display_name} src={normalizeImageUrl(store.avatar)} />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography>
                      {store.display_name || store.shop_name || store.username} {store.verified && '✓'} 
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        @{store.username}
                      </Typography>
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      閲覧数: {(store.views_count || store.view_count || store.followers_count || 0).toLocaleString()}
                    </Typography>
                  }
                />
                {currentUser && currentUser.username !== store.username && (
                  <Button
                    variant={followingStatus[store.id] ? "outlined" : "contained"}
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFollowClick(store.id, store.username);
                    }}
                    sx={{ ml: 2 }}
                  >
                    {followingStatus[store.id] ? "フォロー中" : "フォローする"}
                  </Button>
                )}
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  // クチコミランキングの表示
  const renderReviewsRanking = () => {
    if (reviewsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (rankings.reviews.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <Typography>表示するランキングがありません</Typography>
        </Box>
      );
    }

    return (
      <List sx={{ p: 0 }}>
        {rankings.reviews.filter(Boolean).map((item, index) => {
          const isTop3 = index < 3;
          const rankClass = isTop3 ? `rankings__rank-${index + 1}` : '';
          const itemClass = isTop3 ? `rankings__item-top rankings__item-${index + 1}` : '';

          return (
            <React.Fragment key={item.target_id}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  ...(isTop3 ? rankingsPageStyles.listItemTop : rankingsPageStyles.listItem),
                  flexWrap: 'wrap',  // スマホで詳細ボタンが下に折り返す
                  rowGap: 1,
                }}
                onClick={() => item.username && handleUserClick(item.username)}
                className={itemClass}
              >
                <div className={`rankings__rank ${rankClass}`} style={rankingsPageStyles.rankDiv}>
                  <Typography variant="h6">{index + 1}</Typography>
                </div>
                <ListItemAvatar sx={{ minWidth: 48 }}>
                  <Avatar alt={item.target_name} src={normalizeImageUrl(item.target_avatar)} />
                </ListItemAvatar>
                <ListItemText
                  sx={{ minWidth: 0, flex: '1 1 auto' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography component="span">
                        {item.target_name} {item.verified && '✓'}
                      </Typography>
                      <Chip
                        label={item.target_type === 'corporate' ? '企業' : '投稿ユーザー'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Rating
                          value={item.avg_rating || 0}
                          readOnly
                          size="small"
                          precision={0.1}
                        />
                        <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
                          {(item.avg_rating || 0).toFixed(1)}
                        </Typography>
                      </Box>
                      <Typography component="span" variant="body2" color="text.secondary">
                        クチコミ {item.total_reviews || 0}件
                      </Typography>
                    </Box>
                  }
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/reviews-ranking/${item.target_id}`);
                  }}
                  sx={{ ml: 'auto', flexShrink: 0, alignSelf: 'center' }}
                >
                  詳細
                </Button>
              </ListItem>
              <Divider variant="inset" component="li" />
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          ランキング
        </Typography>
        
        {!currentUser && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography>
              ランキングはゲストユーザー以外が閲覧できます。より詳細な情報を見るには
              <Button color="primary" onClick={() => navigate('/login')}>
                ログイン
              </Button>
              してください。
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            エリア設定
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' }, 
            gap: { xs: 1, sm: 2 } 
          }}>
            <FormControl sx={{ minWidth: { xs: 'auto', sm: 200 }, flex: { sm: 1 } }} size="small">
              <InputLabel id="prefecture-select-label">都道府県</InputLabel>
              <Select
                labelId="prefecture-select-label"
                id="prefecture-select"
                value={selectedPrefecture}
                label="都道府県"
                onChange={handlePrefectureChange}
                disabled={loadingArea}
              >
                <MenuItem value="">全国</MenuItem>
                {prefectures.map((prefecture) => (
                  <MenuItem key={prefecture.code} value={prefecture.code}>
                    {prefecture.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl sx={{ minWidth: { xs: 'auto', sm: 200 }, flex: { sm: 1 } }} size="small">
              <InputLabel id="city-select-label">市区町村</InputLabel>
              <Select
                labelId="city-select-label"
                id="city-select"
                value={selectedCity}
                label="市区町村"
                onChange={handleCityChange}
                disabled={loadingArea || !selectedPrefecture}
              >
                <MenuItem value="">全域</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.code} value={city.code}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button 
              variant="contained" 
              onClick={handleAreaUpdate}
              disabled={loadingArea}
              color="primary"
              sx={{ flexShrink: 0 }}
            >
              更新
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="クチコミランキング" value="reviews" />
            <Tab label="人気投稿ランキング" value="posts" />
            <Tab label="フォロワー数ランキング" value="followers" />
            <Tab label="人気店舗ランキング" value="stores" />
          </Tabs>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {activeTab === 'posts' && renderPostRanking()}
            {activeTab === 'followers' && renderFollowerRanking()}
            {activeTab === 'stores' && renderStoreRanking()}
            {activeTab === 'reviews' && renderReviewsRanking()}
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default Rankings; 